import React, { useEffect, useState } from 'react'
//components
import ExampleContainer from './ExampleContainer'
import {
  Alert,
  BodyText,
  CaptionText,
  PAYPAL_THEME,
} from '@paypalcorp/pp-react'
import Loading from './AiLoading'
import AiGeneragedPill from './AiGeneratedPill'
import Feedback from './Feedback'
import Sparkle from '../../../icons/sparkle'
//syles
import styles from './AiSearch.module.css'
//config
import { faqData } from './config'
import WasThisHelpful from './WasThisHelpful'
import SeeMore from './SeeMore'
import MarkdownContainer from './MarkdownContainer'
import ConversationCard from './ConversationCard'

const { sysColorBackgroundMediumContrast, sysColorAccent4Main } = PAYPAL_THEME

interface AiAnswerSummaryProps {
  handleSuggestionClick: (text: string) => void
  fetchResults: (text: string, uuid: string) => void
  isLoadingSummary: boolean
  answerSummary: string
  errorStatus: string
  setSearchQuery: (text: string) => void
  searchQuery: string
  answerId: string
  searchID: string
  isStreamingComplete: boolean
}
const AiAnswerSummary = ({
  handleSuggestionClick,
  fetchResults,
  isLoadingSummary,
  answerSummary,
  errorStatus,
  setSearchQuery,
  answerId,
  searchQuery,
  searchID,
  isStreamingComplete,
}: AiAnswerSummaryProps) => {
  const [collapseSeeMore, setCollapseSeeMore] = useState(true)
  const [isDislikeSelected, setIsDislikeSelected] = useState(false)
  const [isLikeSelected, setIsLikeSelected] = useState(false)
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false)
  const [feedbackSent, setFeedbackSent] = useState(false)
  const [showSeeMore, setShowSeeMore] = useState(false)

  const [summaryContainerHeight, setSummaryContainerHeight] = useState(0)
  const [summaryTextHeight, setSummaryTextHeight] = useState(0)

  useEffect(() => {
    // resetting feedback state for a new search
    if (isLoadingSummary) {
      setIsDislikeSelected(false)
      setIsLikeSelected(false)
      setIsFeedbackOpen(false)
      setFeedbackSent(false)
    }
  }, [isLoadingSummary])

  useEffect(() => {
    // show link to expand ai summary if text is longer than summary container height
    if (summaryContainerHeight > summaryTextHeight) {
      setShowSeeMore(true)
    }
  }, [summaryContainerHeight, setSummaryTextHeight])

  const fetchAnswer = (text: string) => {
    handleSuggestionClick(text)
    fetchResults(text)
    setSearchQuery(text)
  }
  const closeDialog = () => {
    setIsFeedbackOpen(false)
  }

  return (
    <>
      {errorStatus && (
        <ConversationCard>
          <BodyText data-testid="hc-gen-ai-error-text">
            To get Quick help, try your search again or skip to the search
            results below.
          </BodyText>
        </ConversationCard>
      )}
      {!isLoadingSummary && !answerSummary && !errorStatus && (
        <ExampleContainer
          sectionData={faqData}
          handleClick={fetchAnswer}
          searchID={searchID}
        />
      )}
      {!errorStatus && (!!answerSummary || isLoadingSummary) && (
        <ConversationCard>
          <>
            <div className={styles.showMoreButton}>
              {isStreamingComplete && answerSummary ? (
                <CaptionText
                  className={styles.listTitle}
                  style={{ color: sysColorBackgroundMediumContrast }}
                >
                  Quick answer
                  <AiGeneragedPill />
                </CaptionText>
              ) : (
                <CaptionText
                  style={{ color: sysColorAccent4Main }}
                  className={styles.loadingAnswerTitle}
                >
                  <Sparkle />
                  <span className={styles.ml1}>Generating quick answer...</span>
                </CaptionText>
              )}
              {showSeeMore && (
                <SeeMore
                  isStreamingComplete={isStreamingComplete}
                  collapseSeeMore={collapseSeeMore}
                  answerId={answerId}
                  searchID={searchID}
                  setCollapseSeeMore={setCollapseSeeMore}
                />
              )}
            </div>
            {!isLoadingSummary && !!answerSummary && (
              <>
                <MarkdownContainer
                  answerId={answerId}
                  searchID={searchID}
                  collapseSeeMore={collapseSeeMore}
                  answerSummary={answerSummary}
                  setSummaryTextHeight={setSummaryTextHeight}
                  setSummaryContainerHeight={setSummaryContainerHeight}
                />
                <div className={styles.answerFooterContainer}>
                  <div className={styles.answerFooter}>
                    {isStreamingComplete && (
                      <WasThisHelpful
                        searchQuery={searchQuery}
                        answerId={answerId}
                        searchID={searchID}
                        setIsFeedbackOpen={setIsFeedbackOpen}
                        setIsDislikeSelected={setIsDislikeSelected}
                        setIsLikeSelected={setIsLikeSelected}
                        isLikeSelected={isLikeSelected}
                        isDislikeSelected={isDislikeSelected}
                        /** To remove the unselected button*/
                        hideLike={isDislikeSelected}
                        hideDislike={isLikeSelected}
                        /** To disable the feedback button once it is selected */
                        disableLike={isLikeSelected}
                        disableDisLike={isDislikeSelected}
                      />
                    )}
                  </div>
                  <>
                    {isFeedbackOpen && (
                      <Feedback
                        answerId={answerId}
                        searchID={searchID}
                        closeDialog={closeDialog}
                        setIsFeedbackOpen={setIsFeedbackOpen}
                        setFeedbackSent={setFeedbackSent}
                      />
                    )}
                    {feedbackSent && (
                      <Alert
                        type="success"
                        closeButton={{
                          'aria-label': 'dismiss alert',
                          onClick: () => {
                            setFeedbackSent(false)
                          },
                        }}
                      >
                        Feedback sent
                      </Alert>
                    )}
                  </>
                </div>
              </>
            )}

            {isLoadingSummary && <Loading classes={'bgPurple'} />}
          </>
        </ConversationCard>
      )}
    </>
  )
}

export default AiAnswerSummary
